import * as React from 'react';
import { isDefined, noop } from 'react-financial-charts';
import type { DrawingSharedState, HoverTextConfig, MoreProps } from '@/types/commontypes';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { ClickableCircle, HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { ChannelWithArea, ClickableReactangle, FibChannelShape, InteractiveStraightLine } from '.';
import { retracementsPropetyWithColor } from '@/utils/defaultColors';
import type { ChartId, Levels } from '@/types/commontypes';

/** EachFibChannel / EachTriangle의 onDrag 콜백에서 전달되는 좌표 데이터 */
export interface TriangleDragData {
  startXY?: number[];
  endXY?: number[];
  dy?: number;
  endXY2?: number[];
}

export interface EachFibChannelProps {
  readonly startXY: number[];
  readonly endXY: number[];
  readonly dy: number;
  readonly interactive: boolean;
  readonly selected: boolean;
  readonly hoverText: HoverTextConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly appearance: Record<string, any>;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly endXY2?: number[];
  readonly index?: number;
  readonly uuid: string;
  readonly onDrag: (e: React.MouseEvent, uuid: string, dragData: TriangleDragData) => void;
  readonly onSelect?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly onDragStart?: (uuid: string) => void;
  readonly onHover?: (uuid: string | null, moreProps?: MoreProps) => void;
  readonly locked?: boolean;
  readonly fill?: string;
  readonly stroke?: string;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly chartId?: ChartId;
  readonly sharedState?: DrawingSharedState;
  readonly isAnyShapeEnabled?: boolean;
}

interface EachFibChannelState {
  hover: boolean;
  dragging: boolean;
}

export class EachFibChannel extends React.Component<EachFibChannelProps, EachFibChannelState> {
  public static defaultProps = {
    yDisplayFormat: (d: number) => d.toFixed(2),
    interactive: true,
    selected: false,
    onDrag: noop,
    hoverText: {
      enable: false,
    },
  };

  private dragStart!: { startXY: number[]; endXY: number[]; dy: number; endXY2?: number[] };
  // @ts-expect-error
  private lineInteractive: string;
  private saveNodeType: <T>(type: string) => React.Ref<T>;

  public constructor(props: EachFibChannelProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);

    this.state = {
      hover: false,
      dragging: false,
    };
  }

  public render() {
    const { startXY, endXY, dy, onDoubleClick } = this.props;
    const { interactive, hoverText, appearance, endXY2 } = this.props;
    const {
      edgeFill,
      edgeFill2,
      strokeStyle,
      strokeWidth,
      fill,
      extendLeft,
      extendRight,
      channelLevels,
      strokeDasharray,
      backgroundOpacity,
      isParallel,
      midLine,
    } = appearance;
    const { selected } = this.props;
    const { hover } = this.state;
    const { enable: hoverTextEnabled, ...restHoverTextProps } = hoverText;
    const hoverHandler: {
      onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
      onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
    } = interactive ? { onHover: this.handleHover, onUnHover: this.handleHover } : {};
    const line1Edge =
      isDefined(startXY) && isDefined(endXY) ? (
        <g>
          {this.getEdgeCircle({
            xy: startXY,
            dragHandler: this.handleLine1Edge1Drag,
            cursor: 'react-financial-charts-ns-resize-cursor',
            fill: edgeFill,
            edge: 'line1edge1',
          })}
          {this.getEdgeCircle({
            xy: endXY,
            dragHandler: this.handleLine1Edge2Drag,
            cursor: 'react-financial-charts-ns-resize-cursor',
            fill: edgeFill,
            edge: 'line1edge2',
          })}
        </g>
      ) : null;
    const line2Edge =
      isParallel === true || (endXY2 !== undefined && dy !== undefined && isDefined(dy)) ? (
        <g>
          {this.getEdgeCircle({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            xy: isParallel ? [startXY[0], startXY[1] + dy] : (endXY2 as number[]),
            dragHandler: isParallel ? this.handleLine1Edge1Drag : this.handleChannelHeightChange,
            cursor: 'react-financial-charts-ns-resize-cursor',
            fill: edgeFill2,
            edge: 'line2edge1',
          })}
          {isParallel &&
            endXY &&
            this.getEdgeCircle({
              xy: [endXY[0], endXY[1] + dy],
              dragHandler: this.handleLine1Edge2Drag,
              cursor: 'react-financial-charts-ns-resize-cursor',
              fill: edgeFill2,
              edge: 'line2edge2',
            })}
        </g>
      ) : null;
    const { dragging } = this.state;
    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const levels = (channelLevels ? channelLevels : retracementsPropetyWithColor) as Levels[];
    return (
      <g>
        {isParallel && startXY && endXY ? (
          <>
            <ChannelWithArea
              ref={this.saveNodeType('channel')}
              selected={selected || hover}
              onHover={this.handleHover}
              onUnHover={this.handleHover}
              startXY={startXY}
              endXY={endXY}
              dy={dy}
              strokeStyle={strokeStyle}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              fillStyle={fill}
              interactiveCursorClass="react-financial-charts-move-cursor"
              extendLeft={extendLeft}
              extendRight={extendRight}
              onMouseDown={this.handleShapeMouseDown}
              onDoubleClick={onDoubleClick}
              onDragStart={this.handleDragStart}
              onDrag={this.handleChannelDrag}
              onDragComplete={this.handleDragComplete}
            />
            <InteractiveStraightLine
              selected={selected || hover}
              x1Value={startXY[0]}
              x2Value={endXY[0]}
              y1Value={startXY[1] + dy / 2}
              y2Value={endXY[1] + dy / 2}
              strokeStyle={midLine?.strokeStyle || 'rgb(156, 39, 176)'}
              strokeDasharray={midLine?.strokeDasharray}
              strokeWidth={midLine?.strokeWidth}
              type="LINE"
              extendLeft={extendLeft}
              extendRight={extendRight}
            />
            {/* Mid-line Clickable Rectangles */}
            {this.renderClickableRect('line1', (startXY[0] + endXY[0]) / 2, (startXY[1] + endXY[1]) / 2)}
            {this.renderClickableRect('line2', (startXY[0] + endXY[0]) / 2, (startXY[1] + endXY[1]) / 2 + dy)}

            {/* {this.renderClickableRect("bottom", (x1Value + x2Value) / 2, y2Value,)}
                        {this.renderClickableRect("left", x1Value, (y1Value + y2Value) / 2)}
                        {this.renderClickableRect("right", x2Value, (y1Value + y2Value) / 2)} */}
          </>
        ) : (
          <FibChannelShape
            ref={this.saveNodeType('channel')}
            selected={selected || hover}
            {...hoverHandler}
            startXY={startXY}
            endXY={endXY}
            dy={dy}
            strokeStyle={strokeStyle}
            endXY2={endXY2}
            strokeWidth={strokeWidth}
            fillStyle={fill}
            channelLevels={levels}
            backgroundOpacity={backgroundOpacity}
            extendLeft={extendLeft}
            extendRight={extendRight}
            strokeDasharray={strokeDasharray}
            interactiveCursorClass={this.lineInteractive}
            onDragStart={this.handleDragStart}
            onDrag={this.handleChannelDrag}
            onDragComplete={this.handleDragComplete}
            onMouseDown={this.handleShapeMouseDown}
            onDoubleClick={onDoubleClick}
          />
        )}
        {line1Edge}
        {line2Edge}
        <HoverTextNearMouse show={hoverTextEnabled && hover && !selected} {...restHoverTextProps} />
      </g>
    );
  }

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    if (onSelect && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly getEdgeCircle = ({
    xy,
    dragHandler,
    fill,
    edge,
  }: {
    xy: number[];
    dragHandler: (e: React.MouseEvent, moreProps: MoreProps) => void;
    cursor?: string;
    fill: string;
    edge: string;
  }) => {
    const { hover } = this.state;
    const { appearance } = this.props;
    const { edgeStroke, edgeStrokeWidth, r } = appearance;
    const { selected } = this.props;
    const { onDragComplete, locked } = this.props;
    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;
    return (
      <ClickableCircle
        ref={this.saveNodeType(edge)}
        show={selected || hover || locked}
        cx={xy[0]}
        cy={xy[1]}
        r={lockedR}
        fillStyle={fill}
        strokeStyle={lockedEdgeStorke}
        strokeWidth={lockedStrokeWidth}
        interactiveCursorClass="big-declined-arrow-cursor"
        onDragStart={this.handleDragStart}
        onDrag={dragHandler}
        onDragComplete={onDragComplete}
      />
    );
  };

  private renderClickableRect = (refKey: string, cx: number, cy: number) => {
    const { selected, locked, appearance, onDoubleClick } = this.props;
    const { edgeFill, edgeStrokeWidth, r, edgeStroke } = appearance;
    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;
    const { hover } = this.state;

    return (
      <ClickableReactangle
        key={refKey}
        ref={this.saveNodeType(refKey)}
        show={selected || hover}
        cx={cx}
        cy={cy}
        r={lockedR}
        fillStyle={edgeFill}
        strokeStyle={lockedEdgeStorke}
        strokeWidth={lockedStrokeWidth}
        interactiveCursorClass={'react-financial-charts-ns-resize-cursor'}
        onDragStart={this.handleDragStart}
        onDrag={(e, moreProps) => this.handleNSChannelHeightChange(e, moreProps, refKey)}
        onDragComplete={this.handleDragComplete}
        onDoubleClick={onDoubleClick}
      />
    );
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    this.setState({
      dragging: false,
    });

    const { onDragComplete, locked } = this.props;
    if (onDragComplete === undefined || locked) {
      return;
    }

    onDragComplete(e, moreProps);
  };

  private readonly handleChannelHeightChange = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const { startXY, endXY } = this.dragStart;

    const {
      chartConfig: { yScale },
      mouseXY,
      xScale,
      xAccessor,
      fullData,
    } = moreProps;

    const [mouseX, mouseY] = mouseXY;

    // Convert mouse coords to data space
    const xVal = getXValue(xScale, xAccessor, [mouseX, mouseY], fullData);
    const yVal = yScale.invert(mouseY);

    onDrag(e, uuid, {
      startXY,
      endXY,
      dy: this.dragStart.dy,
      endXY2: [xVal, yVal],
    });
  };

  private readonly handleNSChannelHeightChange = (e: React.MouseEvent, moreProps: MoreProps, key: string) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;
    if (locked) return;

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }

    const { startXY, endXY, dy } = this.dragStart;
    const {
      chartConfig: { yScale },
      mouseXY,
    } = moreProps;
    const startPos = moreProps.startPos!;

    const dyPixels = startPos[1] - mouseXY[1];
    const deltaY = yScale.invert(0) - yScale.invert(dyPixels); // Convert px to data units

    if (key === 'line1') {
      // Move both startXY and endXY by deltaY
      const newStartXY = [startXY[0], startXY[1] + deltaY];
      const newEndXY = [endXY[0], endXY[1] + deltaY];
      const newDy = dy - deltaY;

      onDrag(e, uuid, {
        startXY: newStartXY,
        endXY: newEndXY,
        dy: newDy,
        endXY2: this.dragStart.endXY2,
      });
    } else {
      // It's line2, so we update only dy
      const newDy = dy + deltaY;

      onDrag(e, uuid, {
        startXY,
        endXY,
        dy: newDy,
        endXY2: this.dragStart.endXY2,
      });
    }
  };

  private readonly handleLine1Edge2Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;
    if (locked) return;
    const { endXY } = this.dragStart;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const {
      mouseXY,
      xAccessor,
      xScale,
      fullData,
      chartConfig: { yScale },
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const x1 = xScale(endXY[0]);
    const y1 = yScale(endXY[1]);

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);
    if (hovering) {
      onDrag(e, uuid, {
        startXY: this.dragStart.startXY,
        endXY: [newX1Value, newY1Value],
        dy: this.dragStart.dy,
        endXY2: this.dragStart.endXY2,
      });
    }
  };

  private readonly handleLine1Edge1Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;
    if (locked) return;
    const { startXY } = this.dragStart;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const {
      mouseXY,
      xAccessor,
      xScale,
      fullData,
      chartConfig: { yScale },
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const x1 = xScale(startXY[0]);
    const y1 = yScale(startXY[1]);

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);
    if (hovering) {
      onDrag(e, uuid, {
        startXY: [newX1Value, newY1Value],
        endXY: this.dragStart.endXY,
        dy: this.dragStart.dy,
        endXY2: this.dragStart.endXY2,
      });
    }
  };

  private readonly handleChannelDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, locked } = this.props;
    if (locked) return;
    const { startXY, endXY, endXY2 } = this.dragStart;
    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
      mouseXY,
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    const x1 = xScale(startXY[0]);
    const y1 = yScale(startXY[1]);
    const x2 = xScale(endXY[0]);
    const y2 = yScale(endXY[1]);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(xScale, xAccessor, [x2 - dx, y2 - dy], fullData);
    const newY2Value = yScale.invert(y2 - dy);
    const deltaX = newX2Value - endXY[0];
    const deltaY = newY2Value - endXY[1];

    const newEndXY2 = endXY2 ? [endXY2[0] + deltaX, endXY2[1] + deltaY] : undefined;

    this.setState({ dragging: true });
    if (!hovering) return;
    onDrag(e, uuid, {
      startXY: [newX1Value, newY1Value],
      endXY: [newX2Value, newY2Value],
      dy: this.dragStart.dy,
      endXY2: newEndXY2,
    });
  };

  private readonly handleDragStart = () => {
    const { startXY, endXY, dy, endXY2, onDragStart, uuid, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    this.setState({ dragging: true });
    this.dragStart = {
      startXY,
      endXY,
      dy,
      endXY2,
    };
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps.hovering || false;
    if (this.state.hover !== hovering) {
      this.setState({
        hover: hovering,
      });
      if (onHover) {
        onHover(hovering ? (uuid ?? null) : null, moreProps);
      }
    }
  };
}
